import React, { useState } from 'react';
import { Table, InputGroup, FormControl, Pagination, Form } from 'react-bootstrap';
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useSortBy,
  useResizeColumns,
  useFlexLayout,
  usePagination
} from 'react-table';
import styled from 'styled-components';
import tw from 'twin.macro';

const Container = tw.div`container bg-white rounded border border-gray-200 shadow-lg p-4`;
const Title = tw.h1`text-xl font-bold text-blue-600 mb-2 text-center`;
const TableWrapper = tw.div`mt-4 overflow-x-auto`;

const StyledTable = styled(Table)`
  ${tw`min-w-full`}
  font-size: 12px;
  font-family: 'Arial', sans-serif;
  border: 1px solid #ccc;
  border-collapse: collapse;
  width: 100%;

  thead {
    ${tw`bg-gray-200`}
    
    th {
      ${tw`border border-gray-300 text-center`}
      background-color: #f0f0f0;
      font-weight: bold;
      white-space: normal; /* Allow text wrapping */
      text-overflow: ellipsis;
      overflow: hidden;
      padding: 8px;
      position: relative;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
    }
  }

  tbody {
    tr {
      ${tw`border-b border-gray-200`}
      
      &:hover {
        ${tw`bg-gray-100`}
      }
    }

    td {
      ${tw`border border-gray-300 text-center py-2 px-4`}
      padding: 8px;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
    }

    .align-right {
      text-align: right;
    }

    .align-left {
      text-align: left;
    }
  }
`;

const FilterInput = styled(FormControl)`
  ${tw`border border-gray-300`}
  height: 30px;
  font-size: 12px; /* Adjusted font size */
`;

const ResizableHeader = ({ className, column }) => {
  const { getHeaderProps, getSortByToggleProps, isSorted, isSortedDesc, canResize } = column;

  return (
    <th className={className} {...getHeaderProps()}>
      <div style={{ position: 'relative', width: '100%' }}>
        <span>{column.render('Header')}</span>
        {column.canSort && (
          <button
            className="sort-button"
            {...getSortByToggleProps()}
            style={{ padding: '0', marginLeft: '5px', fontSize: '10px', background: 'none', border: 'none' }}
          >
            {isSorted ? (isSortedDesc ? '↓' : '↑') : '↕'}
          </button>
        )}
        {canResize && (
          <div
            {...column.getResizerProps()}
            className="resizer"
            style={{ right: 0, position: 'absolute', top: 0, bottom: 0, cursor: 'col-resize', width: '10px' }}
          />
        )}
      </div>
    </th>
  );
};

const PaginationContainer = styled.div`
  ${tw`flex justify-between items-center mt-4`}
  .pagination {
    ${tw`m-0 p-0`}
  }
  .pagination li {
    ${tw`inline-block`}
  }
`;

const OrdersTable = ({ orders }) => {
  const formatAmount = (value) => {
    return parseFloat(value).toFixed(2);
  };

  const columns = React.useMemo(
    () => [
      { Header: 'Order ID', accessor: 'order_id', width: 150, align: 'right' },
      { Header: 'Exchange Order ID', accessor: 'exchange_order_id', width: 150, align: 'right' },
      { Header: 'Exchange Code', accessor: 'exchange_code', width: 130, align: 'left' },
      { Header: 'Stock Code', accessor: 'stock_code', width: 110, align: 'left' },
      { Header: 'Product Type', accessor: 'product_type', width: 120, align: 'left' },
      { Header: 'Action', accessor: 'action', width: 90, align: 'left' },
      { Header: 'Order Type', accessor: 'order_type', width: 110, align: 'left' },
      { Header: 'Stoploss', accessor: 'stoploss', width: 90, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Quantity', accessor: 'quantity', width: 90, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Price', accessor: 'price', width: 80, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Validity', accessor: 'validity', width: 90, align: 'left' },
      { Header: 'Disclosed Quantity', accessor: 'disclosed_quantity', width: 150, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Expiry Date', accessor: 'expiry_date', width: 110, align: 'right' },
      { Header: 'Right', accessor: 'right', width: 80, align: 'left' },
      { Header: 'Strike Price', accessor: 'strike_price', width: 110, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Average Price', accessor: 'average_price', width: 120, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Cancelled Quantity', accessor: 'cancelled_quantity', width: 150, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Pending Quantity', accessor: 'pending_quantity', width: 150, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Status', accessor: 'status', width: 90, align: 'left' },
      { Header: 'User Remark', accessor: 'user_remark', width: 120, align: 'left' },
      { Header: 'Order Datetime', accessor: 'order_datetime', width: 150, align: 'right' },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    setGlobalFilter,
    state,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    pageOptions,
    setPageSize,
  } = useTable(
    {
      columns,
      data: orders || [],
      initialState: { pageIndex: 0, pageSize: 10, globalFilter: '' }, // Initial state with pageIndex and pageSize
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    useFlexLayout,
    usePagination // Include usePagination hook
  );

  const [paginationIndex, setPaginationIndex] = useState(0);
  const paginationSize = 15;

  // Effect to update paginationIndex when pageIndex changes
  React.useEffect(() => {
    const newPaginationIndex = Math.floor(state.pageIndex / paginationSize);
    setPaginationIndex(newPaginationIndex);
  }, [state.pageIndex]);

  // Function to get pagination items
  const getPaginationItems = () => {
    const items = [];
    const start = paginationIndex * paginationSize;
    const end = Math.min(start + paginationSize, pageOptions.length);

    for (let i = start; i < end; i++) {
      items.push(
        <Pagination.Item key={i} onClick={() => gotoPage(i)} active={i === state.pageIndex}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return items;
  };

  // Function to move to the next pagination group
  const nextPaginationGroup = () => {
    if ((paginationIndex + 1) * paginationSize < pageOptions.length) {
      setPaginationIndex(paginationIndex + 1);
      gotoPage((paginationIndex + 1) * paginationSize);
    }
  };

  // Function to move to the previous pagination group
  const previousPaginationGroup = () => {
    if (paginationIndex > 0) {
      setPaginationIndex(paginationIndex - 1);
      gotoPage((paginationIndex - 1) * paginationSize);
    }
  };

  return (
    <Container>
      <Title>Order List</Title>
      <InputGroup className="my-3">
        <FilterInput
          value={state.globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
        />
      </InputGroup>
      <TableWrapper>
        <StyledTable {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <ResizableHeader
                    key={column.id}
                    column={column}
                    className="text-center"
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      className={cell.column.align === 'right' ? 'align-right' : 'align-left'}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </StyledTable>
      </TableWrapper>

      {/* Pagination Controls */}
      <PaginationContainer>
        <div>
          <span>
            Page{' '}
            <strong>
              {state.pageIndex + 1} of {pageOptions.length}
            </strong>{' '}
          </span>
        </div>
        <Pagination size="sm"> {/* Small pagination */}
          <Pagination.First onClick={previousPaginationGroup} disabled={paginationIndex === 0} />
          <Pagination.Prev onClick={() => previousPage()} disabled={!canPreviousPage} />
          {getPaginationItems()}
          <Pagination.Next onClick={() => nextPage()} disabled={!canNextPage} />
          <Pagination.Last onClick={nextPaginationGroup} disabled={(paginationIndex + 1) * paginationSize >= pageOptions.length} />
        </Pagination>
        <div>
          <span>
            Rows per page:{' '}
            <Form.Select
              size="sm" // Small select
              value={state.pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              style={{ width: 'auto', display: 'inline-block' }} // Adjusted width
            >
              {[10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Form.Select>
          </span>
        </div>
      </PaginationContainer>
    </Container>
  );
};

export default OrdersTable;
